import dayjs from "dayjs";

import { DAYJS_FORMAT, DAYJS_WITH_TIME_FORMAT_FULL, OVERLAP_DATA, MONTHS, PERMS, ROMAN_PERMS, YEAR_DAYS, MIN_DATE_ROMAN, LENKA_PERMS, MIN_DATE_MAKUDEROVI, EMPTY_DESC } from "~/const-files/const";
import { VISITED_DISTRICTS, VISITED_STATES } from "~/const-files/map";
import { COMMON_DATA } from "~/data/common";
import { ICalendarEvent, IEvent, IGallery, IGalleryItems, TTag, ILatestYearVisits, IGalleryItem, ITracksList, IVisitedItem, ICommonDataDiff, IEventsData, ISearch, IGalleryFolder, TSeachType, IEventDayItem,
	TService, ILogin, ICalendarLegendItem, IPersonItem, IGalleryFilter, TPersonsRest, TPhotosTypes, ISearchPhotoItem, IInThisDateItem, IInThisDateYearItem,
	IInThisDateSubItem, ICovid } from "~/interfaces";
import { getEvents } from "~/providers/php-api";
import { createEmptyDistances, getBBoxesCenter, getDataLink, getDateHours, getGalleryLink, getStateHolidays, getYearsBack, hasTimeHours, includeArrayItems, sortStringArray, sortStringArrayWithName, transYearsBack } from "~/utils/utils";
import { calculateTrackDistances, createGallery, createTrackListItem, mergeDistances, NONE_DATE_KEY } from "~/utils/events";
import { getPersonData, getPersonsData } from "~/persons/persons";
import { PERSONS_KEYS } from "~/persons/makuderovi";
import { TAGS } from "~/const-files/tags";
import { globalStore } from "~/stores/global";

interface IEventFolder {
	folder: string;
	title: string;
	isYear: boolean;
}

interface IPhotoIndexItem {
	origItem: IGalleryItem;
	gallery: IGallery;
	// titulek ve vysledcich hledani - rok + nazev udalosti
	title: string;
}

interface IMonthEvent {
	[key: string]: IEvent[];
}

interface IFolderGallery {
	folder: string;
	count: number;
	title: string;
	isYear: boolean;
}

// globalni data
let eventsFolders: Array<IEventFolder> = [];
let galleryFolders: Array<IFolderGallery> = [];
let allEvents: Array<IEvent> = [];
let guessGalleries: Array<IGallery> = [];
let calendarEvents: Array<ICalendarEvent> = [];
let allGalleries: Array<IGallery> = [];
let galleryItems: IGalleryItems = {};
let allTags: Array<string> = [];
const tracksList: ITracksList = {
	0: [],
};
let allPhotoTags: Array<string> = [];
let photoStatsPersons: Array<IPersonItem> = [];
// indexy pro rychle hledani - seo pri nacteni, fotky az pri prvnim hledani (nezdrzuje start)
let photosIndexReady = false;
let photosIndex: Array<IPhotoIndexItem> = [];
let photosByPerson = new Map<string, Array<IPhotoIndexItem>>();
let photosByTag = new Map<string, Array<IPhotoIndexItem>>();
let eventsBySeo = new Map<string, IEvent>();
let galleryIndexBySeo = new Map<string, number>();
const MONTHS_PAD_LEN = 2;

export function getCalendarLegendItems(service: TService, isAdmin: boolean) {
	const items: Array<ICalendarLegendItem> = [];

	if (isAdmin) {
		items.push({
			title: "Všechny události",
			className: "default",
			isDefault: true,
		});
		items.push({
			title: isAdmin ? "Admin" : "Události",
			className: "default",
			isAdmin: true,
		});
		items.push({
			title: "Veřejné",
			className: "calendar3",
			isPublic: true,
		});

		if (service === "makuderovi") {
			items.push({
				title: "Rodina",
				className: "calendar1",
				permissions: PERMS.RODINA,
			});
			items.push({
				title: "Děti",
				className: "calendar2",
				permissions: PERMS.DETI,
			});
		} else if (service === "roman") {
			items.push({
				title: "Kámoši",
				className: "calendar1",
				permissions: ROMAN_PERMS.KAMOSI,
			});
			items.push({
				title: "Seznam",
				className: "calendar4",
				permissions: ROMAN_PERMS.SEZNAM,
			});
			items.push({
				title: "Rodina",
				className: "calendar2",
				permissions: ROMAN_PERMS.RODINA,
			});
		} else if (service === "lenka") {
			items.push({
				title: "Rodina",
				className: "calendar2",
				permissions: LENKA_PERMS.RODINA,
			});
		}
	} else {
		items.push({
			title: "Události",
			className: "calendar3",
		});
	}

	items.push({
		title: "Státní svátky",
		className: "state-holiday",
	});

	return items;
}

// kalendar vyzaduje primo 01-01
function createEvent(event: IEvent, service: TService, loginData: ILogin): ICalendarEvent {
	const hasGallery = !!event.galleryEvent;
	const hasTracks = event.allTracks.length > 0;
	let appendix = "";

	if (hasGallery) {
		appendix += "📷";
	}

	if (hasTracks) {
		appendix += "🚶";
	}

	const title = `${event.title}${appendix.length > 0 ? " " : ""}${appendix}`;
	const classNames = ["my-event", "calendar-event"];

	if (loginData.isAdmin) {
		if (event.public) {
			classNames.push("calendar3");
		} else if (event.groups) {
			if (service === "makuderovi") {
				if (event.groups.includes(PERMS.RODINA)) {
					classNames.push("calendar1");
				}

				if (event.groups.includes(PERMS.DETI)) {
					classNames.push("calendar2");
				}
			} else if (service === "roman") {
				if (event.groups.includes(ROMAN_PERMS.KAMOSI)) {
					classNames.push("calendar1");
				}

				if (event.groups.includes(ROMAN_PERMS.SEZNAM)) {
					classNames.push("calendar4");
				}

				if (event.groups.includes(ROMAN_PERMS.RODINA)) {
					classNames.push("calendar2");
				}
			} else if (service === "lenka") {
				if (event.groups.includes(LENKA_PERMS.RODINA)) {
					classNames.push("calendar2");
				}
			}
		} else {
			classNames.push("default");
		}
	} else {
		classNames.push("calendar3");
	}

	return {
		title,
		...event.date && event.toDate
			? {
				start: event.date,
				end: dayjs(event.toDate).add(1, 'day').format(DAYJS_FORMAT),
			}
			: {
				date: event.date,
			},
		classNames,
		extendedProps: {
			seo: event.seo,
			date: event.date,
			toDate: event.toDate || "",
			public: event.public,
			groups: event.groups,
		},
	};
}

// bez pole udalosti (i prazdneho) by web zustal v nekonzistentnim stavu -> chyba
async function loadEventsByType(service: TService, loginData: ILogin): Promise<IEventsData> {
	const eventsData = await getEvents(service, loginData);
	const data = eventsData.data;

	if (!Array.isArray(data?.events)) {
		throw new Error(`Nepodařilo se načíst události${eventsData.error ? `: ${eventsData.error}` : ""}`);
	}

	return {
		events: data.events,
		meta: {
			photoTags: data.meta?.photoTags || [],
			personsPhotos: data.meta?.personsPhotos || {},
			sourceItems: data.meta?.sourceItems || [],
		},
	};
}

// datumy ve formatu YYYY-MM-DD HH:mm:ss staci porovnat jako stringy - bez parsovani pres dayjs
function compareDateStrings(aDate: string, bDate: string) {
	return aDate < bDate
		? -1
		: aDate > bDate
			? 1
			: 0;
}

const YEAR_STR_LEN = 4;

function getYearFromDate(date: string) {
	return date.slice(0, YEAR_STR_LEN);
}

function getDateWithoutYear(date: string) {
	return date.slice(MIN_DATE_MAKUDEROVI.indexOf("-"), MIN_DATE_MAKUDEROVI.length);
}

// "v tento den" testuje stejna data pro vsechny udalosti -> rozparsovane hodnoty si pamatujeme
interface IParsedDate {
	date: dayjs.Dayjs;
	unix: number;
	year: number;
	month: number;
	day: number;
}

const parsedDatesCache = new Map<string, IParsedDate>();
const derivedDatesCache = new Map<string, IParsedDate>();

function createParsedDate(date: dayjs.Dayjs): IParsedDate {
	return {
		date,
		unix: date.unix(),
		year: date.year(),
		month: date.get("month"),
		day: date.get("date"),
	};
}

function getParsedDate(dateStr: string) {
	let parsed = parsedDatesCache.get(dateStr);

	if (!parsed) {
		parsed = createParsedDate(dayjs(dateStr));
		parsedDatesCache.set(dateStr, parsed);
	}

	return parsed;
}

// odvozene datum (posun roku apod.) - cache podle klice
function getDerivedDate(key: string, create: () => dayjs.Dayjs) {
	let parsed = derivedDatesCache.get(key);

	if (!parsed) {
		parsed = createParsedDate(create());
		derivedDatesCache.set(key, parsed);
	}

	return parsed;
}

export function clearEventsData() {
	// reset
	eventsFolders = [];
	galleryFolders = [];
	allEvents = [];
	guessGalleries = [];
	calendarEvents = [];
	allGalleries = [];
	galleryItems = {};
	allTags = [];
	allPhotoTags = [];

	for (const key of Object.keys(tracksList)) {
		delete tracksList[key];
	}

	tracksList[0] = [];

	for (const person of photoStatsPersons) {
		person.photoCount = 0;
		person.photoBest = null;
	}

	photoStatsPersons = [];
	photosIndexReady = false;
	photosIndex = [];
	photosByPerson = new Map();
	photosByTag = new Map();
	eventsBySeo = new Map();
	galleryIndexBySeo = new Map();
	parsedDatesCache.clear();
	derivedDatesCache.clear();
}

function addToIndexMap<T>(map: Map<string, Array<T>>, key: string, item: T) {
	const list = map.get(key);

	if (!list) {
		map.set(key, [item]);
	// osoba muze byt u fotky omylem vicekrat
	} else if (list[list.length - 1] !== item) {
		list.push(item);
	}
}

function createSeoIndexes() {
	allEvents.forEach(event => !eventsBySeo.has(event.seo) && eventsBySeo.set(event.seo, event));
	allGalleries.forEach((gallery, ind) => !galleryIndexBySeo.has(gallery.event.seo) && galleryIndexBySeo.set(gallery.event.seo, ind));
}

function createPhotosIndex() {
	if (photosIndexReady) {
		return;
	}

	photosIndexReady = true;

	for (const gallery of allGalleries) {
		const title = `${getYearFromDate(gallery.event.date)} ${gallery.event.title}`;

		for (const origItem of gallery.items) {
			photosIndex.push({
				origItem,
				gallery,
				title,
			});
		}
	}

	// od nejnovejsiho, fotky bez data ("") na konec - sort je stabilni, stejne datum zachova poradi
	photosIndex.sort((aItem, bItem) => compareDateStrings(bItem.origItem.date || "", aItem.origItem.date || ""));

	// podmnoziny zachovavaji serazeni
	for (const indexItem of photosIndex) {
		for (const person of indexItem.origItem.persons || []) {
			addToIndexMap(photosByPerson, person.key, indexItem);
		}

		for (const tag of indexItem.origItem.tags || []) {
			addToIndexMap(photosByTag, tag, indexItem);
		}
	}
}

function processEvents({ events: yearEvents, meta: eventsMeta }: IEventsData, service: TService, loginData: ILogin) {
	const foldersGalleriesYears: Array<IFolderGallery> = [];
	const foldersGalleriesOthers: Array<IFolderGallery> = [];
	const eventsFoldersYears: Array<IEventFolder> = [];
	const eventsFoldersOthers: Array<IEventFolder> = [];
	const monthEvents: IMonthEvent = {};
	const allEventsTags: Array<string> = [];
	// rucni nastaveni fotek - jen v datech adminske skupiny
	const sourceConfs = new Map((eventsMeta.sourceItems || []).map(item => [item.seo, item.conf]));
	const covidEvents: Array<IEvent> = [];

	// projdeme a nastavime
	for (const yearEvent of yearEvents) {
		let galleriesCount = 0;

		// slozky
		if (yearEvent.isYear) {
			eventsFoldersYears.push({
				folder: yearEvent.folder,
				isYear: true,
				title: yearEvent.folder,
			});
		} else {
			eventsFoldersOthers.push({
				folder: yearEvent.folder,
				isYear: false,
				title: yearEvent.title || yearEvent.folder,
			});
		}

		// jednotlive roky
		for (const preEvent of yearEvent.events) {
			const event: IEvent = {
				...preEvent,
				allTracks: [],
				distances: createEmptyDistances(),
				search: {
					title: preEvent.title.toLocaleLowerCase(),
					desc: "",
					galleryTitles: "",
				},
			};
			const folder = yearEvent.folder;
			let allDesc: Array<string> = event.desc;

			// vsechny eventy
			allEvents.push(event);

			if (event.tags && event.tags.includes(TAGS.COVID)) {
				covidEvents.push(event);
			}

			// mame galerii
			const galleryData = createGallery({
				event,
				folder,
				isYear: yearEvent.isYear,
				type: loginData.type,
				sourceConfs,
			});

			// tagy
			if (event.tags) {
				for (const tag of event.tags) {
					if (!allEventsTags.includes(tag)) {
						allEventsTags.push(tag);
					}
				}
			}

			if (event.desc.length === 0 && !("days" in event) && !galleryData) {
				event.desc.push(EMPTY_DESC);
			}

			// mame galerii
			if (galleryData) {
				// celkovy pocet galerii
				galleriesCount++;
				// titulky
				event.search.galleryTitles = galleryData.meta.galleryTitles;

				if (!(folder in galleryItems)) {
					galleryItems[folder] = [];
				}

				galleryItems[folder].push(galleryData.gallery);
				allGalleries.push(galleryData.gallery);

				if (galleryData.gallery.type === "month-item") {
					if (!monthEvents[event.date]) {
						monthEvents[event.date] = [];
					}

					monthEvents[event.date].push(event);
				}

				// pridame do hadani - nesmi mit month item
				if (yearEvent.isYear) {
					guessGalleries.push(galleryData.gallery);
				}
			}

			if (event.days) {
				for (const day of event.days) {
					// defaultne nic neni
					day.items = [];
					day.photosCount = 0;
					day.videosCount = 0;
					day.distances = createEmptyDistances();
					// vsechny popisky
					allDesc = allDesc.concat(day.desc);

					if (day.tracks && day.tracks.length > 0) {
						// pridame datum
						day.tracks.forEach(track => {
							track.date = day.date || "";
							calculateTrackDistances(track);

							if (!track.date) {
								/* eslint-disable-next-line */
								console.log(["Error", track.date, event, track]);
							}

							event.allTracks.push(track);
						});

						const trackListItem = createTrackListItem(event, galleryData?.gallery, day.tracks);

						mergeDistances(day.distances, trackListItem.distances);

						if (!(folder in tracksList)) {
							tracksList[folder] = [];
						}

						tracksList[folder].push(trackListItem);
						tracksList["0"].push(trackListItem);
					}
				}

				let prolog: IEventDayItem = null;
				let epilog: IEventDayItem = null;

				// priradime fotky
				if (galleryData) {
					// klice datesObjDays prirazene ke dnum
					const usedDayKeys = new Set<string>();

					for (const day of event.days) {
						switch (day.type) {
							case "prolog":
								prolog = day;
								prolog.items = galleryData.meta.prologItems || [];
								break;

							case "day":
							case "custom": {
								const name = day.type === "day"
									? day.date
									: day.customName;

								// priradime fotky podle dnu/vlastniho jmena
								if (name in galleryData.meta.datesObjDays) {
									usedDayKeys.add(name);
									day.items = galleryData.meta.datesObjDays[name];
									day.photosCount = day.items.filter(item => !item.isVideo).length;
									day.videosCount = day.items.length - day.photosCount;
								}
								break;
							}

							case "epilog":
								epilog = day;
								epilog.items = galleryData.meta.epilogItems;
								break;

							default:
						}
					}

					// fotky s datem, ktere nema svuj den - pred akci do prologu, po akci do epilogu, ostatni jako fotky bez datumu
					const eventFrom = event.date || "";
					const eventTo = event.toDate || eventFrom;
					const beforeItems: Array<IGalleryItem> = [];
					const afterItems: Array<IGalleryItem> = [];
					let othersItems: Array<IGalleryItem> = galleryData.meta.othersItems;

					for (const [dayKey, dayKeyItems] of Object.entries(galleryData.meta.datesObjDays)) {
						if (dayKey === NONE_DATE_KEY || usedDayKeys.has(dayKey)) {
							continue;
						}

						const isDate = /^\d{4}-\d{2}-\d{2}$/u.test(dayKey);

						if (isDate && eventFrom && dayKey < eventFrom) {
							beforeItems.push(...dayKeyItems);
						} else if (isDate && eventTo && dayKey > eventTo) {
							afterItems.push(...dayKeyItems);
						} else {
							othersItems = othersItems.concat(dayKeyItems);
						}
					}

					const missingItems: Array<IGalleryItem> = [];

					if (beforeItems.length) {
						if (prolog) {
							prolog.items = beforeItems.concat(prolog.items);
						} else if (epilog) {
							epilog.items = beforeItems.concat(epilog.items);
						} else {
							missingItems.push(...beforeItems);
						}
					}

					if (afterItems.length) {
						if (epilog) {
							epilog.items = epilog.items.concat(afterItems);
						} else if (prolog) {
							prolog.items = prolog.items.concat(afterItems);
						} else {
							missingItems.push(...afterItems);
						}
					}

					if (othersItems.length) {
						// mame oba, dame na konec - nebo jenom epilog
						if ((prolog && epilog) || (!prolog && epilog)) {
							epilog.items = epilog.items.concat(othersItems);
						} else if (prolog) {
							prolog.items = prolog.items.concat(othersItems);
						} else {
							missingItems.push(...othersItems);
						}
					}

					if (missingItems.length) {
						const allLen = missingItems.length;
						const photosCount = missingItems.filter(item => !item.isVideo).length;

						// nemame nic, vytvorime
						event.days.push({
							type: "custom",
							desc: ["Chybějící fotky"],
							items: missingItems,
							distances: createEmptyDistances(),
							photosCount,
							videosCount: allLen - photosCount,
						});
					}

					//event.days && console.log(galleryData.meta.datesObjDays);
				}

				if (prolog) {
					prolog.photosCount = prolog.items.filter(item => !item.isVideo).length;
					prolog.videosCount = prolog.items.length - prolog.photosCount;
				}

				if (epilog) {
					epilog.photosCount = epilog.items.filter(item => !item.isVideo).length;
					epilog.videosCount = epilog.items.length - epilog.photosCount;
				}
			}

			if (event.tracks) {
				event.tracks.forEach(track => {
					// pridame datum
					track.date = track.date || event.date || "";
					calculateTrackDistances(track);
					event.allTracks.push(track);
				});

				const trackListItem = createTrackListItem(event, galleryData?.gallery, event.tracks);

				if (!(folder in tracksList)) {
					tracksList[folder] = [];
				}

				tracksList[folder].push(trackListItem);
				tracksList["0"].push(trackListItem);
			}

			// prehled tracku u udalosti
			if (event.allTracks.length) {
				for (const track of event.allTracks) {
					mergeDistances(event.distances, track.distances);
				}
			}

			// pridame vsechny texty
			event.search.desc = allDesc.join(",").toLocaleLowerCase();

			// nemame souradnice -> spocitame z tracku
			if (!event.coords && event.allTracks.length) {
				event.coords = getBBoxesCenter(event.allTracks.map(track => track.bbox));
			}

			// do kalendare
			if (!event.skipCalendar) {
				calendarEvents.push(createEvent(event, service, loginData));
			}
		}

		// pocty galerii
		if (yearEvent.isYear) {
			foldersGalleriesYears.push({
				folder: yearEvent.folder,
				count: galleriesCount,
				isYear: true,
				title: yearEvent.folder,
			});
		} else {
			foldersGalleriesOthers.push({
				folder: yearEvent.folder,
				count: galleriesCount,
				isYear: false,
				title: yearEvent.title || yearEvent.folder,
			});
		}
	}

	// seradime pro kalendar
	sortStringArrayWithName<IEventFolder>(eventsFoldersYears, "folder");
	sortStringArrayWithName<IEventFolder>(eventsFoldersOthers, "folder");
	eventsFoldersYears.reverse();
	eventsFolders = eventsFoldersYears.concat(eventsFoldersOthers);
	// seradime gallerie
	sortStringArrayWithName<IFolderGallery>(foldersGalleriesYears, "folder");
	sortStringArrayWithName<IFolderGallery>(foldersGalleriesOthers, "folder");
	foldersGalleriesYears.reverse();
	// galerie
	galleryFolders = foldersGalleriesYears.concat(foldersGalleriesOthers).filter(item => item.count > 0);
	// nastavime tagy
	allTags = allEventsTags;

	allPhotoTags = [...eventsMeta.photoTags];

	// seradime
	sortStringArray(allTags);
	sortStringArray(allPhotoTags);

	// nastavime mesice
	const monthItems = Object.keys(monthEvents);

	// seradime
	sortStringArray(monthItems);

	// vsechny udalosti
	const allMonthEvents: Array<IEvent> = [];

	for (const monthDate of monthItems) {
		for (const monthEvent of monthEvents[monthDate]) {
			allMonthEvents.push(monthEvent);
		}
	}

	for (let ind = 0, max = allMonthEvents.length; ind < max; ind++) {
		const monthEvent = allMonthEvents[ind];

		monthEvent.monthData = {};

		if (ind > 0) {
			monthEvent.monthData.prevMonth = allMonthEvents[ind - 1].seo;
		}

		if (ind !== max -1) {
			monthEvent.monthData.nextMonth = allMonthEvents[ind + 1].seo;
		}
	}

	createSeoIndexes();

	// doplnime foto data do uzivatelu - predpocitane v buildu
	for (const [personKey, personPhotos] of Object.entries(eventsMeta.personsPhotos)) {
		const person = getPersonData(personKey);

		if (person) {
			const { bestPhoto } = personPhotos;

			person.photoCount = personPhotos.count;
			person.photoBest = bestPhoto
				? allGalleries[galleryIndexBySeo.get(bestPhoto.seo)]?.items.find(item => item.name === bestPhoto.name) || null
				: null;
			photoStatsPersons.push(person);
		}
	}

	if (covidEvents.length > 0) {
		const lastEvent = covidEvents[covidEvents.length - 1];
		const covidData: ICovid = {
			startDate: covidEvents[0].date,
			endDate: lastEvent.date,
			items: [],
		};

		for (const covidEvent of covidEvents) {
			const filtered = covidData.items.filter(item => item.date === covidEvent.date);
			const desc = covidEvent.desc.includes(EMPTY_DESC) && covidEvent.desc.length === 1
				? []
				: covidEvent.desc;

			if (filtered.length) {
				filtered[0].title += `, ${covidEvent.title}`;
				filtered[0].desc = filtered[0].desc.concat(desc);
			} else {
				covidData.items.push({
					date: covidEvent.date,
					title: covidEvent.title,
					desc,
				});
			}
		}

		globalStore.getState().setCovidData(covidData);
	}
}

// pri chybe vyhazuje vyjimku a data udalosti zustanou prazdna (zadny castecny stav)
export async function loadEvents(service: TService, loginData: ILogin) {
	// nacteme
	const eventsData = await loadEventsByType(service, loginData);

	clearEventsData();

	try {
		processEvents(eventsData, service, loginData);
	} catch (exc) {
		clearEventsData();
		throw exc;
	}
}

export function getSeo(seo: string) {
	return seo.toLowerCase();
}

export function getPhotoTags() {
	return allPhotoTags;
}

function getPersonsKeys(persons: Array<IPersonItem>, personsRest: TPersonsRest, onlyPeople: boolean) {
	return persons.filter(item => {
		if (personsRest === "none") {
			return true;
		}

		return onlyPeople
			? !item.isFluffy
			: true;
	}).map(item => item.key);
}

interface IsearchPhotos {
	persons: Array<string>;
	personsRest: TPersonsRest;
	fromDate: dayjs.Dayjs;
	toDate: dayjs.Dayjs;
	photoTags: Array<string>;
	photoPrivate: boolean;
	newest: boolean;
	photoType: TPhotosTypes;
	// vek prvni zadane osoby, null = bez omezeni
	age?: number | null;
}

export function searchPhotos({
	persons = [],
	personsRest = "none",
	fromDate,
	toDate,
	photoTags = [],
	photoPrivate = false,
	newest = false,
	photoType = "all",
	age = null,
}: IsearchPhotos) {
	const output: Array<ISearchPhotoItem> = [];
	const personMappings = getPersonsData(persons);
	const onlyPeople = personMappings.filter(item => !item.isFluffy).length === persons.length;
	const ageFilter = age !== null && persons.length > 0;
	let searchFromDate = fromDate;
	let searchToDate = toDate;

	if (ageFilter) {
		const birthdate = personMappings.find(item => item.key === persons[0])?.birthdate;

		if (!birthdate) {
			return output;
		}

		// zuzime rozsah dat na obdobi, kdy mela osoba zadany vek
		const ageFromDate = dayjs(birthdate).add(age, "years");
		const ageToDate = dayjs(birthdate).add(age + 1, "years").subtract(1, "day");

		searchFromDate = ageFromDate.isAfter(fromDate) ? ageFromDate : fromDate;
		searchToDate = ageToDate.isBefore(toDate) ? ageToDate : toDate;
	}

	createPhotosIndex();

	const fromDateStr = searchFromDate.format(DAYJS_FORMAT);
	const toDateStr = searchToDate.format(DAYJS_FORMAT);
	// fotky porovnavame vcetne celeho posledniho dne
	const photoToDateStr = searchToDate.add(1, "day").format(DAYJS_FORMAT);
	// prochazime nejkratsi seznam - osoby (musi byt vsechny, krome "jakekoliv") a tagy (staci jeden z nich - sjednoceni)
	let candidates = photosIndex;
	const requiredLists = personsRest === "anyone"
		? []
		: persons.map(personKey => photosByPerson.get(personKey) || []);

	if (photoTags.length === 1) {
		requiredLists.push(photosByTag.get(photoTags[0]) || []);
	} else if (photoTags.length > 1) {
		// sjednoceni tagu - filtrem indexu zachovame serazeni
		const tagItems = new Set(photoTags.flatMap(tag => photosByTag.get(tag) || []));

		requiredLists.push(photosIndex.filter(indexItem => tagItems.has(indexItem)));
	}

	for (const list of requiredLists) {
		if (list.length < candidates.length) {
			candidates = list;
		}
	}

	const len = candidates.length;

	for (let ind = 0; ind < len; ind++) {
		// index je serazeny od nejnovejsiho
		const { origItem, gallery, title } = candidates[newest ? ind : len - 1 - ind];
		// jednodenni galerie nema datesRangeTo
		const galleryToDate = gallery.datesRangeTo || gallery.datesRangeFrom;

		if (gallery.datesRangeFrom > toDateStr || galleryToDate < fromDateStr) {
			continue;
		}

		// u veku musi mit fotka datum v danem obdobi
		if (ageFilter && (!origItem.date || origItem.date < fromDateStr || origItem.date >= photoToDateStr)) {
			continue;
		}

		if (persons.length > 0 && !(origItem.persons && includeArrayItems({
			inpArray: getPersonsKeys(origItem.persons, personsRest, onlyPeople),
			testArray: persons,
			lenCheck: personsRest === "exclusively",
			fullCheck: personsRest !== "anyone",
		}))) {
			continue;
		}

		if (photoTags.length > 0 && !(origItem.tags && includeArrayItems({
			inpArray: origItem.tags,
			testArray: photoTags,
			fullCheck: false,
		}))) {
			continue;
		}

		if ((photoPrivate && !origItem.private) || (photoType === "photos" && origItem.isVideo) || (photoType === "videos" && !origItem.isVideo)) {
			continue;
		}

		output.push({
			origItem,
			item: {
				...origItem,
				coords: null,
				title,
			},
		});
	}

	return output;
}


interface IGetCalendarEvents {
	publicEvent?: boolean;
	adminEvent?: boolean;
	groups?: Array<number>;
}

export function getCalendarEvents({
	publicEvent = false,
	adminEvent = false,
	groups = [],
}: IGetCalendarEvents) {
	const curYear = dayjs().year();
	let output: Array<ICalendarEvent> = [];

	// statni svatky
	eventsFolders.forEach(folderItem => {
		if (folderItem.isYear) {
			const year = parseFloat(folderItem.folder);
			const yearHolidays = getStateHolidays(year);

			output = output.concat(yearHolidays);
		}
	});

	// +1 rok
	output = output.concat(getStateHolidays(curYear + 1));

	// nase udalosti
	output = output.concat(calendarEvents.filter(event => {
		if (adminEvent) {
			return !event.extendedProps.public && (!event.extendedProps.groups || event.extendedProps.groups.length === 0);
		}

		if (publicEvent) {
			return event.extendedProps.public;
		}

		if (groups.length) {
			if (!event.extendedProps.groups || event.extendedProps.groups.length === 0) {
				return false;
			}

			for (const testGroup of groups) {
				if (event.extendedProps.groups.includes(testGroup)) {
					return true;
				}
			}

			return false;
		}

		if (!publicEvent && groups.length === 0) {
			return true;
		}

		return false;
	}));

	return output;
}

export function getAllGalleries() {
	return allGalleries;
}

export function getGuessGalleries() {
	return guessGalleries;
}

export function getGalleryBySeo(seo: string) {
	return allGalleries[galleryIndexBySeo.get(getSeo(seo))];
}

export function getGalleriesDataBySeo(seo: string) {
	const len = allGalleries.length;
	const selInd = galleryIndexBySeo.get(getSeo(seo)) ?? -1;
	const gallery: IGallery = allGalleries[selInd] ?? null;
	let prevGalleryLink = "";
	let nextGalleryLink = "";

	if (selInd > 0) {
		const prevEvent = allGalleries[selInd - 1].event;

		prevGalleryLink = getGalleryLink(prevEvent.seo, prevEvent.date, prevEvent.toDate);
	}

	if (selInd >= 0 && selInd < len - 1) {
		const nextEvent = allGalleries[selInd + 1].event;

		nextGalleryLink = getGalleryLink(nextEvent.seo, nextEvent.date, nextEvent.toDate);
	}

	return {
		prevGalleryLink,
		nextGalleryLink,
		gallery,
	};
}

export function getGalleryFolderItems() {
	return galleryFolders;
}

export function getGalleryFolders(year = "") {
	const output: Array<IGalleryFolder> = galleryFolders.map(item => ({
		title: item.title,
		url: getDataLink(item.folder),
		active: item.folder === year,
	}));

	return output;
}

export function getLatestGalleryYearFolder() {
	const yearItems = galleryFolders.filter(item => item.isYear);

	yearItems.sort((aItem, bItem) => bItem.folder.localeCompare(aItem.folder));

	return yearItems.length > 0
		? yearItems[0]
		: null;
}

export function getFolderTitle(folder: string) {
	const filtered = galleryFolders.filter(item => item.folder === folder);

	if (filtered.length) {
		return filtered[0].title;
	}

	return "";
}

export function getDataByYears(folders: Array<string>) {
	let output: Array<IGallery> = [];

	for (const folder of folders) {
		if (galleryItems[folder]) {
			output = output.concat(galleryItems[folder]);
		}
	}

	return output;
}

function getFiltersCount(filterData: IGalleryFilter) {
	let filterCount = 0;

	if (filterData.title) {
		filterCount++;
	}

	if (filterData.perms > 0) {
		filterCount++;
	}

	if (filterData.rest !== "all") {
		filterCount++;
	}

	if (filterData.tags.length > 0) {
		filterCount++;
	}

	if (filterData.months.length > 0) {
		filterCount++;
	}

	return filterCount;
}

function getMonthsIds(filterData: IGalleryFilter) {
	const monthsIds: Array<string> = [];

	if (filterData.months.length > 0) {
		// month item, mesic je uprostred jako rok-mesic-den
		for (const month of filterData.months) {
			const ind = MONTHS.indexOf(month) + 1;

			monthsIds.push(`-${ind.toString().padStart(MONTHS_PAD_LEN, "0")}-`);
		}
	}

	return monthsIds;
}

function compareTitle(title: string, compareStr: string) {
	const titleLc = title.toLocaleLowerCase();
	const compareLc = compareStr.toLocaleLowerCase();

	return titleLc.includes(compareLc);
}

export function searchYearGalleryItems(filterData: IGalleryFilter) {
	let items: Array<IGallery> = [];
	let allVideos: Array<IGalleryItem> = [];
	const allItems = getDataByYears(filterData.folders);
	const filterCount = getFiltersCount(filterData);
	const filterCurrentYear = filterData.folders.length === 1;
	const monthsIds = getMonthsIds(filterData);

	if (filterCount > 0) {
		items = allItems.filter(yearItem => {
			let currentCount = 0;

			if (filterData.tags.length > 0 && includeArrayItems({
				inpArray: yearItem.event.tags || [],
				testArray: filterData.tags,
			})) {
				currentCount++;
			}

			if (filterData.title && compareTitle(yearItem.event.title, filterData.title)) {
				currentCount++;
			}

			if (filterData.perms > 0) {
				// admin
				if ([PERMS.ADMIN, ROMAN_PERMS.ADMIN, LENKA_PERMS.ADMIN].includes(filterData.perms)) {
					// nemame groups + neni public
					if (!("groups" in yearItem.event) && !yearItem.event.public) {
						currentCount++;
					}
				} else if (yearItem.event.groups?.includes(filterData.perms)) {
					currentCount++;
				}
			}

			if (filterData.rest !== "all") {
				let check = false;

				switch (filterData.rest) {
					case "public":
						check = yearItem.event.public;
						break;

					case "days":
						check = !!yearItem.event.days;
						break;

					case "months":
						check = "monthData" in yearItem.event;
						break;

					case "tracks":
						check = yearItem.event.allTracks.length > 0;
						break;

					default:
				}

				if (check) {
					currentCount++;
				}
			}

			// omezeni na mesice
			if (monthsIds.length > 0) {
				for (const monthId of monthsIds) {
					// bud hlavni datum obsahuje mesic nebo posledni datum obsahuje mesic
					if (yearItem.event.date.includes(monthId) || (yearItem.event.toDate && yearItem.event.toDate.includes(monthId))) {
						currentCount++;
						break;
					}
				}
			}

			return filterCount === currentCount;
		});
	} else {
		items = allItems;
	}

	if (filterData.folders.length === 1) {
		if (filterData.newest) {
			items = items.reverse();
		}
	} else {
		items.sort((aItem, bItem) => filterData.newest
			? compareDateStrings(bItem.event.date, aItem.event.date)
			: compareDateStrings(aItem.event.date, bItem.event.date));
	}

	for (const gallery of items) {
		const filtered = gallery.items.filter(item => item.isVideo);

		if (filtered.length) {
			allVideos = allVideos.concat(filtered);
		}
	}

	return {
		items,
		allVideos,
		/* eslint-disable-next-line */
		filterKey: Math.random().toString(16),
		filterCurrentYear,
	};
}

// galerie posledniho roku s galeriemi - jen u nich se hlida novy obsah
function getLatestYearGalleries(): Array<IGallery> {
	const latestYear = getLatestGalleryYearFolder();

	return latestYear
		? getDataByYears([latestYear.folder])
		: [];
}

// pocty polozek galerii posledniho roku
export function getLatestGalleryVisits() {
	const output: ILatestYearVisits = {};

	getLatestYearGalleries().forEach(item => {
		output[item.event.seo] = item.items.length;
	});

	return output;
}

// novy obsah = galerie je neshlednuta nebo ma vic polozek, nez kolik bylo shlednuto
function isNewGalleryCount(visitData: ILatestYearVisits, seo: string, count: number) {
	return count > (visitData[seo] ?? 0);
}

// ma nejaka galerie posledniho roku novy obsah
export function hasGalleryNews(visitData: ILatestYearVisits) {
	return getLatestYearGalleries().some(item => isNewGalleryCount(visitData, item.event.seo, item.items.length));
}

export function checkGalleryNews(visitData: ILatestYearVisits, gallery: IGallery) {
	if (!getLatestYearGalleries().some(item => item.event.seo === gallery.event.seo)) {
		return false;
	}

	return isNewGalleryCount(visitData, gallery.event.seo, gallery.items.length);
}

// pocty se jen zvysuji - s nizsim opravnenim je polozek mene a nechceme pak znovu hlasit novy obsah
export function mergeGalleryVisits(visitData: ILatestYearVisits, newData: ILatestYearVisits) {
	const output = {
		...visitData,
	};

	for (const [seo, count] of Object.entries(newData)) {
		output[seo] = Math.max(output[seo] ?? 0, count);
	}

	return output;
}

export function updateVisitDataByGallery(visitData: ILatestYearVisits, gallery: IGallery) {
	return mergeGalleryVisits(visitData, {
		[gallery.event.seo]: gallery.items.length,
	});
}

export function getGalleryDataByEvent(event: IEvent) {
	if (event?.galleryEvent) {
		return getGalleryBySeo(event.seo);
	}

	return null;
}

export function getEventBySeo(seo: string) {
	return eventsBySeo.get(getSeo(seo));
}

export function getGalleriesByTags(gallerySeo: string, tags: Array<TTag>): Array<IGallery> {
	const searchTags = tags || [];
	const compSeo = getSeo(gallerySeo);

	if (!searchTags.length) {
		return [];
	}

	const allItems = getAllGalleries();

	return allItems.filter(galleryItem => {
		const galleryTags = galleryItem.event.tags || [];

		for (const tag of galleryTags) {
			if (searchTags.includes(tag) && galleryItem.event.seo !== compSeo) {
				return true;
			}
		}

		return false;
	// potrebujeme od nejnovejsiho
	}).reverse();
}

export function getAllTags() {
	return allTags;
}

function isLeapYear(year: number) {
	// gregoriansky kalendar
	/* eslint-disable-next-line no-magic-numbers */
	return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

// datum (YYYY-MM-DD), kdy udalost v nekterem z predchozich let pripadla na testovany den; jinak null
function getEventMatchDate(fromDateStr: string, toDateStr: string, checkDate: string) {
	const fromDate = getParsedDate(fromDateStr);
	const toDate = toDateStr
		? getParsedDate(toDateStr)
		: null;
	const isFebruary29 = checkDate.includes(OVERLAP_DATA.february29);
	// 29. unor je uz v predchozim prestupnem roce, ostatni dny posuneme o rok zpet
	const curDate = isFebruary29
		? getParsedDate(checkDate)
		: getDerivedDate(`${checkDate}|prev-year`, () => dayjs(checkDate).subtract(1, "year"));

	// from - prev year
	if (fromDate.year <= curDate.year) {
		if (toDate) {
			// nastavime stejny rok
			for (let year = fromDate.year; year <= toDate.year; year++) {
				// 29. unor existuje jen v prestupnych letech (dayjs by z nej udelal 1. brezen)
				if (isFebruary29 && !isLeapYear(year)) {
					continue;
				}

				const yearDate = getDerivedDate(`${checkDate}|check-year|${year}`, () => curDate.date.year(year));

				if (yearDate.unix >= fromDate.unix && yearDate.unix <= toDate.unix) {
					return yearDate.date.format(DAYJS_FORMAT);
				}
			}

			return null;
		}

		return curDate.month === fromDate.month && curDate.day === fromDate.day
			? fromDate.date.format(DAYJS_FORMAT)
			: null;
	}

	return null;
}

// fotky podle jejich vlastniho datumu (fotky bez datumu dostaly v createGallery datum udalosti) - stejny den a mesic, rok nejpozdeji maxYear
function getGalleryItemsByDate(gallery: IGallery, monthDay: string, maxYear: string) {
	return gallery.items.filter(item => item.date
		&& getDateWithoutYear(item.date) === monthDay
		&& getYearFromDate(item.date) <= maxYear);
}

// neprestupny rok pro prevod datumu na poradi dne v roce bez 29. unora
const NO_LEAP_YEAR = 2023;

// poradi dne v kalendari bez 29. unora (29. 2. = 28. 2.) - posun o stejny pocet dni v prestupnem i neprestupnem roce
function getNoLeapDayIndex(date: dayjs.Dayjs) {
	const monthDay = date.format("MM-DD") === "02-29"
		? "02-28"
		: date.format("MM-DD");
	const yearStart = dayjs(`${NO_LEAP_YEAR}-01-01`);

	return (date.year() * YEAR_DAYS) + dayjs(`${NO_LEAP_YEAR}-${monthDay}`).diff(yearStart, "day");
}

function getDateFromNoLeapDayIndex(dayIndex: number) {
	return dayjs(`${NO_LEAP_YEAR}-01-01`).add(dayIndex % YEAR_DAYS, "day").year(Math.floor(dayIndex / YEAR_DAYS));
}

// datum, kdy byl Martinek stejne stary jako Tomasek k danemu dni (podle datumu narozeni v osobach);
// posun je v kalendari bez 29. unora - narozeniny vzdy sedi na narozeniny, jedina nepravidelnost je 29. unor;
// vracime o rok pozdeji, protoze v tento den v rezimu Martinek ukazuje fotky z predchoziho roku (viz getInThisDateData)
export function getMartinekDate(curDate: dayjs.Dayjs) {
	const martinBirthdate = dayjs(getPersonData(PERSONS_KEYS.MAR_MAK).birthdate);
	const tomasBirthdate = dayjs(getPersonData(PERSONS_KEYS.TOM_MAK).birthdate);
	const shift = getNoLeapDayIndex(tomasBirthdate) - getNoLeapDayIndex(martinBirthdate);

	return getDateFromNoLeapDayIndex(getNoLeapDayIndex(curDate) - shift + YEAR_DAYS).format(DAYJS_FORMAT);
}

function personsIncludeMartinek(persons: Array<IPersonItem>) {
	return persons && persons.filter(item => item.key === PERSONS_KEYS.MAR_MAK).length > 0;
}

// stejna fotka ve vice galeriich (stejny cas) se ukaze jen jednou - v jedne galerii jde vzdy o ruzne fotky (serie)
function isDuplicatePhoto(item: IGalleryItem, shownPhotos: Map<string, string>) {
	if (!hasTimeHours(item.date)) {
		return false;
	}

	// rucne zadany cas bez sekund - shodu potvrdime i rozmery
	const hasSeconds = item.date.length >= DAYJS_WITH_TIME_FORMAT_FULL.length;
	const photoKey = `${item.date}|${item.isVideo ? "video" : "photo"}${hasSeconds ? "" : `|${item.width}x${item.height}`}`;
	const gallerySeo = shownPhotos.get(photoKey);

	if (gallerySeo === undefined) {
		shownPhotos.set(photoKey, item.eventSeo);

		return false;
	}

	return gallerySeo !== item.eventSeo;
}

function hasDayContent(day: IEventDayItem) {
	return !!(day.title || day.desc?.length || day.privateDesc?.length);
}

// den akce podle datumu (i bez titulku, ale s obsahem); kdyz den nic nema a akce nema vlastni popis, pouzijeme uvod (prolog)
function getEventDay(event: IEvent, date: string) {
	const monthDay = getDateWithoutYear(date);
	const day = event.days?.find(dayItem => dayItem.date && getDateWithoutYear(dayItem.date) === monthDay && hasDayContent(dayItem));

	if (day) {
		return day;
	}

	const hasEventDesc = event.desc.some(desc => desc !== EMPTY_DESC) || event.privateDesc?.length > 0;

	return hasEventDesc
		? null
		: event.days?.find(dayItem => dayItem.type === "prolog" && hasDayContent(dayItem)) || null;
}

export function getInThisDateData(date?: string, isMartinek?: boolean) {
	let items: Array<IInThisDateItem> = [];
	const galleries: Array<IGallery> = [];
	const mainDateObj = dayjs(date);
	const testYear = mainDateObj.year() - 1;
	const allTestDates: Array<string> = [];
	const allGalleriesSeos: Array<string> = [];
	const yearItems: Array<IInThisDateYearItem> = [];
	const yearsObj: { [key: string]: Array<IInThisDateItem>; } = {};
	const datesMap = new Map<string, Array<IInThisDateSubItem>>();
	// dny udalosti bez fotek - musi mit vlastni polozku v prehledu po rocich
	const eventsDates = new Set<string>();
	const shownPhotos = new Map<string, string>();
	const curYear = dayjs().year();
	const maxYear = (mainDateObj.year() - 1).toString();
	let lastLeapYear = mainDateObj.year() - 1;

	while (!isLeapYear(lastLeapYear)) {
		lastLeapYear--;
	}

	// 29. unor testujeme v poslednim prestupnem roce
	const february29 = `${lastLeapYear}${OVERLAP_DATA.february29}`;

	if (date) {
		if (date.includes(OVERLAP_DATA.february29)) {
			allTestDates.push(february29);
		} else {
			// 29. unor ukazeme 28. unora v letech, kdy neni
			if (date.includes(OVERLAP_DATA.february28) && !isLeapYear(mainDateObj.year())) {
				allTestDates.push(february29);
			}

			allTestDates.push(date);
		}
	}

	function addEventItem(dateKey: string, subItem: IInThisDateSubItem) {
		const yearKey = getYearFromDate(dateKey);

		if (!datesMap.has(yearKey)) {
			datesMap.set(yearKey, []);
		}

		datesMap.get(yearKey).push(subItem);
		eventsDates.add(dateKey);
	}

	for (const event of allEvents) {
		if (event.skipThisDay || (isMartinek && getYearFromDate(event.date) !== testYear.toString())) {
			continue;
		}

		const yearStr = getYearFromDate(event.date);

		if (!datesMap.has(yearStr)) {
			datesMap.set(yearStr, []);
		}

		for (const testDate of allTestDates) {
			let dateItems: Array<IInThisDateItem> = [];

			if (event.gallery) {
				if (isMartinek && !personsIncludeMartinek(event.gallery.persons)) {
					continue;
				}

				let filteredItems = getGalleryItemsByDate(event.gallery, getDateWithoutYear(testDate), maxYear);

				if (isMartinek) {
					filteredItems = filteredItems.filter(item => item.persons && item.persons.filter(personItem => personItem.key === PERSONS_KEYS.MAR_MAK).length > 0);
				}

				if (filteredItems.length) {
					if (!allGalleriesSeos.includes(event.seo)) {
						galleries.push(event.gallery);
						allGalleriesSeos.push(event.seo);
					}

					const year = parseInt(getYearFromDate(event.date), 10);

					dateItems = filteredItems.map(galleryItem => {
						const hours = getDateHours(galleryItem.date || event.date);
						const hoursStr = hours
							? `${hours}, `
							: "";
						const itemDate = galleryItem.date || event.date || "";

						return {
							date: itemDate,
							gallery: {
								...galleryItem,
								// vlastni titulek
								title: galleryItem.isVideo
									? galleryItem.title
									: event.title,
								subTitle: `${hoursStr}${getYearsBack(year, curYear, true)}`,
								coords: null,
								date: itemDate,
							},
						} as IInThisDateItem;
					});
				} else {
					const tags = event.tags || [];

					// mesice u deti
					if (tags.includes(TAGS.MESICE_MARTINEK) || tags.includes(TAGS.MESICE_TOMASEK)) {
						// chceme pouze pro posledni datum
						const testMonthDate = getDerivedDate(`${event.date}|month-end`, () => dayjs(event.date).add(1, "month").subtract(1, "day"));
						const otherDestDate = getDerivedDate(`${testDate}|set-year|${testMonthDate.year}`, () => dayjs(testDate).set("year", testMonthDate.year));

						if (testMonthDate.unix === otherDestDate.unix && testMonthDate.year.toString() <= maxYear) {
							addEventItem(testMonthDate.date.format(DAYJS_FORMAT), {
								event,
							});
						}
					}
				}
			// sedi casy, mame popis, nemame galerii - pro martinka nechceme udalosti
			} else if ((event.desc.length || event.privateDesc?.length || !!event.days) && (!isMartinek || (isMartinek && event.tags && event.tags.includes(TAGS.MARTINEK_DENIK)))) {
				const matchDate = getEventMatchDate(event.date, event.toDate, testDate);

				if (matchDate) {
					addEventItem(matchDate, {
						event,
						day: getEventDay(event, matchDate),
					});
				}
			}

			if (dateItems.length > 0) {
				const uniqueItems = dateItems.filter(dateItem => !isDuplicatePhoto(dateItem.gallery, shownPhotos));

				items = items.concat(uniqueItems);

				for (const curDateItem of uniqueItems) {
					const curDate = curDateItem.date.slice(0, MIN_DATE_ROMAN.length);
					let saveItem: IInThisDateItem = curDateItem;

					if (!(curDate in yearsObj)) {
						yearsObj[curDate] = [];
					}

					const dateObj = dayjs(curDateItem.date);
					const hasTime = hasTimeHours(curDateItem.date);
					const timeStr = hasTime
						? dateObj.format("H:mm")
						: "";

					saveItem = {
						...saveItem,
						gallery: {
							...saveItem.gallery,
							subTitle: timeStr,
						},
					};

					yearsObj[curDate] = yearsObj[curDate].concat(saveItem);
				}

				const curDay = getEventDay(event, testDate);

				if (event.gallery.dateType === "more-years") {
					const newYearStr = getYearFromDate(dateItems[0].date);
					const curItems = datesMap.get(newYearStr) || [];

					curItems.push({
						event,
						day: curDay,
					});
					datesMap.set(newYearStr, curItems);
				} else {
					const curItems = datesMap.get(yearStr);

					curItems.push({
						event,
						day: curDay,
					});
					datesMap.set(yearStr, curItems);
				}
			}
		}
	}

	// seradime podle - nejnovejsi rok, ve stejnem roce od rana do vecera
	items.sort((aItem, bItem) => {
		const aDate = aItem.date || "";
		const bDate = bItem.date || "";

		return compareDateStrings(getYearFromDate(bDate), getYearFromDate(aDate)) || compareDateStrings(aDate, bDate);
	});

	// dny, kdy byly jen udalosti bez fotek
	for (const eventDate of eventsDates) {
		if (!(eventDate in yearsObj)) {
			yearsObj[eventDate] = [];
		}
	}

	const yearItemsKeys = Object.keys(yearsObj);

	yearItemsKeys.sort((aItem, bItem) => {
		const aParts = aItem.split("-");
		const bParts = bItem.split("-");

		if (aParts[0] === bParts[0]) {
			const aNewStr = aParts.reverse().join("");
			const bNewStr = bParts.reverse().join("");

			return aNewStr.localeCompare(bNewStr);
		}

		return bItem.localeCompare(aItem);
	});

	for (const yearKey of yearItemsKeys) {
		const curYearItems = yearsObj[yearKey];
		const dateObj = dayjs(yearKey);
		const yearsDiff = mainDateObj.year() - dateObj.year();
		const yearsStr = yearsDiff > 0
			? ` (${transYearsBack(yearsDiff)})`
			: "";
		const dayStr = dateObj.format("dddd D. M. YYYY");

		// seradime ve dnu
		curYearItems.sort((aItem, bItem) => {
			const aHasHours = hasTimeHours(aItem.date);
			const bHasHours = hasTimeHours(bItem.date);

			if (!aHasHours && !bHasHours) {
				return 0;
			} else if (aHasHours && !bHasHours) {
				return 1;
			} else if (bHasHours && !aHasHours) {
				return -1;
			}

			return dayjs(aItem.date).unix() - dayjs(bItem.date).unix();
		});

		const beforeSubItems = datesMap.get(getYearFromDate(yearKey)) || [];

		// eventy nahoru
		beforeSubItems.sort((aItem, bItem) => {
			const hasAGallery = !!aItem.event.gallery;
			const hasBGallery = !!bItem.event.gallery;

			if (hasAGallery && !hasBGallery) {
				return 1;
			} else if (!hasAGallery && hasBGallery) {
				return -1;
			}

			return 0;
		});

		const subItems = beforeSubItems.filter(eventItem => {
			// preskocime mesice
			if (eventItem.event.monthData) {
				return false;
			}

			const tags = eventItem.event.tags || [];

			// roky deti preskocime
			if (tags.includes(TAGS.DETI_ROKY)) {
				return false;
			}

			if (eventItem.event.gallery && (tags.includes(TAGS.MESICE_MARTINEK) || tags.includes(TAGS.MESICE_TOMASEK))) {
				const testMonthDate = dayjs(eventItem.event.date).add(1, "month").subtract(1, "day").format(DAYJS_FORMAT);
				const filtered = allTestDates.filter(testDateItem => getDateWithoutYear(testDateItem) === getDateWithoutYear(testMonthDate));

				return filtered.length > 0;
			}

			if (!eventItem.day) {
				// galerie bez popisku
				if (eventItem.event.gallery && (eventItem.event.desc.length === 0 || (eventItem.event.desc.length === 1 && eventItem.event.desc[0] === EMPTY_DESC))) {
					return false;
				}

				// galerie s vetsim casem
				if (eventItem.event.gallery && eventItem.event.toDate && !eventItem.event.gallery && eventItem.event.desc.length > 0) {
					// vratime to pouze pro zacatek
					return allTestDates.includes(eventItem.event.date);
				}

				// 28. a 29. unor ve stejnem roce - udalost jen u sveho dne
				if ((eventItem.event.date.includes(OVERLAP_DATA.february29) && yearKey.includes(OVERLAP_DATA.february28))
					|| (eventItem.event.date.includes(OVERLAP_DATA.february28) && !eventItem.event.toDate && yearKey.includes(OVERLAP_DATA.february29))) {
					return false;
				}
			}

			return true;
		});

		// den bez fotek, jehoz udalosti se odfiltrovaly
		if (curYearItems.length === 0 && subItems.length === 0) {
			continue;
		}

		yearItems.push({
			date: yearKey,
			items: curYearItems,
			title: `${dayStr}${yearsStr}`,
			subItems,
		});
	}

	return {
		items,
		yearItems,
		galleries,
	};
}

export function getTracksYearsList() {
	return tracksList;
}

export function diffVisitedArrays(original: Array<string>, compare: Array<IVisitedItem>) {
	const diff: Array<IVisitedItem> = [];

	compare.forEach(item => {
		if (original.indexOf(item.id) === -1) {
			diff.push(item);
		}
	});

	return diff;
}

export function getGetWorldMapDiff() {
	const visitedStates = diffVisitedArrays(VISITED_STATES, COMMON_DATA.visitedStates);
	const visitedDistricts = diffVisitedArrays(VISITED_DISTRICTS, COMMON_DATA.visitedDistricts);

	return {
		change: visitedStates.length > 0 || visitedDistricts.length > 0,
		visitedStates,
		visitedDistricts,
	} as ICommonDataDiff;
}

interface ISearchEvents {
	query: string;
	type: TSeachType;
	tags: Array<string>;
}

export function searchEvents({
	query = "",
	type = "title",
	tags = [],
}: ISearchEvents): ISearch {
	const lcQuery = query.toLowerCase();
	let results: Array<IEvent> = [];
	let loadAllEvents = true;

	if (query) {
		loadAllEvents = false;
		results = allEvents.filter(item => {
			switch (type) {
				case "title":
					return item.search.title.indexOf(lcQuery) !== -1;

				case "desc":
					return item.search.desc.indexOf(lcQuery) !== -1;

				case "gallery":
					return item.search.galleryTitles.indexOf(lcQuery) !== -1;

				case "all":
					return item.search.title.indexOf(lcQuery) !== -1
						|| item.search.desc.indexOf(lcQuery) !== -1
						|| item.search.galleryTitles.indexOf(lcQuery) !== -1;

				default:
			}

			return false;
		});
	}

	if (tags.length > 0) {
		if (loadAllEvents) {
			results = allEvents;
		}

		results = results.filter(item => item.tags && includeArrayItems({
			inpArray: item.tags,
			testArray: tags,
		}));
	}

	return {
		query,
		results,
	};
}
